/**
 * Carte « profil illustré » — affichée juste sous les indicateurs.
 *
 * Reprend toute l'information de l'ancien drawer « Profil politique »
 * (les 4 axes bruts + leur lecture) et y ajoute le courant politique, sous
 * une forme portraiturée : nom du courant en didone, archétype, lecture en
 * prose, et 4 jauges bipolaires (gauche bleu / droite rouge).
 *
 * Gating inchangé : axes dès T4, courant à partir de T8 (cristallisation).
 */

import type { PoliticalProfile, WorldState } from "../engine/types";
import { classifyCourant, COURANT_NAME } from "../engine/courant";
import { ARCHETYPE_NAME, classifyArchetypeKey } from "../engine/archetypes";
import { pledgeVerdict } from "../engine/pledges";
import { useGame } from "../store/useGame";

interface Props {
  state: WorldState;
}

/** Tour de révélation du courant — aligné sur la cristallisation T8. */
const COURANT_REVEALED_FROM_TURN = 8;
const PROFILE_VISIBLE_FROM_TURN = 4;
const AXIS_THRESHOLD = 0.1;

type AxisKey = keyof PoliticalProfile;

interface AxisDef {
  key: AxisKey;
  /** Thème de l'axe, affiché en kicker. */
  theme: string;
  /** Pôle négatif (valeur < 0). */
  left: string;
  /** Pôle positif (valeur > 0). */
  right: string;
}

const AXES: AxisDef[] = [
  {
    key: "economic",
    theme: "Économie",
    left: "interventionniste",
    right: "libéral",
  },
  {
    key: "societal",
    theme: "Société",
    left: "progressiste",
    right: "conservateur",
  },
  {
    key: "international",
    theme: "International",
    left: "européiste",
    right: "souverainiste",
  },
  {
    key: "authority",
    theme: "Autorité",
    left: "libertaire",
    right: "autoritaire",
  },
];

function leanWord(axis: AxisDef, v: number): string {
  if (Math.abs(v) < AXIS_THRESHOLD) return "équilibré";
  return v < 0 ? axis.left : axis.right;
}

export function PoliticalProfileCard({ state }: Props) {
  const turn = state.turn;
  if (turn < PROFILE_VISIBLE_FROM_TURN) return null;

  const profile = state.polity.profile;
  const courantRevealed = turn >= COURANT_REVEALED_FROM_TURN;
  const courant = COURANT_NAME[classifyCourant(profile)];
  const archetype = ARCHETYPE_NAME[classifyArchetypeKey(profile)];

  const leanings = AXES.filter(
    (a) => Math.abs(profile[a.key]) >= AXIS_THRESHOLD,
  ).map((a) => leanWord(a, profile[a.key]));
  const reading =
    leanings.length === 0
      ? "Profil encore indéfini : vos arbitrages n'ont pas tranché."
      : `Une silhouette ${leanings.join(", ")}.`;

  return (
    <section className="political-profile" aria-label="Profil politique">
      <div className="ppc-inner">
        <h2 className="ppc-rubric">Profil politique</h2>
        <PledgeLine />
        <div className="ppc-body">
          <div className="ppc-portrait">
            {courantRevealed ? (
              <>
                <span className="ppc-courant-kicker">Votre courant</span>
                <span className="ppc-courant">{courant}</span>
                <span className="ppc-archetype">{archetype}</span>
              </>
            ) : (
              <span className="ppc-pending">
                Votre couleur politique se cristallise vers le mi-mandat.
              </span>
            )}
            <p className="ppc-reading">{reading}</p>
          </div>

          <ul className="ppc-axes">
            {AXES.map((axis) => {
              const v = profile[axis.key];
              const pct = ((v + 1) / 2) * 100;
              const direction = v >= 0 ? "right" : "left";
              return (
                <li
                  key={axis.key}
                  className="ppc-axis"
                  title={`${axis.theme} : ${axis.left} ↔ ${axis.right}`}
                >
                  <span className="ppc-axis-head">
                    <span className="ppc-axis-theme">{axis.theme}</span>
                    <span className="ppc-axis-lean">{leanWord(axis, v)}</span>
                  </span>
                  <span className="ppc-axis-track" aria-hidden="true">
                    <span className="ppc-axis-zero" />
                    <span
                      className="ppc-axis-fill"
                      data-direction={direction}
                      style={{
                        left: `${Math.min(50, pct)}%`,
                        width: `${Math.abs(pct - 50)}%`,
                      }}
                    />
                  </span>
                  <span className="ppc-axis-value">
                    {v >= 0 ? "+" : ""}
                    {v.toFixed(2)}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </section>
  );
}

/**
 * Contrat de campagne — rappel permanent de la promesse sous le titre de
 * la carte profil : « ce que je promets » vit à côté de « ce que je suis ».
 */
function PledgeLine() {
  const pledges = useGame((s) => s.pledges);
  if (pledges.length === 0) return null;
  return (
    <div className="ppc-pledges">
      {pledges.map((p) => {
        const verdict = pledgeVerdict(p.progress);
        return (
          <div key={p.id} className="ppc-pledge" data-tone={verdict.tone}>
            <span className="ppc-pledge-kicker">Promesse</span>
            <span className="ppc-pledge-label">{p.label}</span>
            <span className="ppc-pledge-verdict">{verdict.label}</span>
          </div>
        );
      })}
    </div>
  );
}
